import type { ReactNode } from "react";

/**
 * The shared shell for every inline Reveal banner (spec §7, §9: inline, not a
 * modal). Owns the ca-reveal animation, the outer row, and the right half
 * (the Next control). Each Loop supplies only its own left-hand sentence as
 * children: whether the guess was correct and the outcome's specifics.
 *
 * `note` is the player's own pre-commit reason (GRA-135), shown back quoted
 * and muted right after that sentence when one was left; omitted entirely
 * when it was skipped, so a Round played without a note reads exactly as it
 * did before this field existed.
 *
 * `setupLine` is the reading of the Context (GRA-141), which sits between
 * the outcome and the note: what the chart showed, then what the player
 * themselves said about it. A caller passes either a plain string (the old
 * shape, still supported) or a `ReactNode` such as `<SetupLine setup={…} />`
 * once it wants the candle/pattern terms linked.
 *
 * `verdict` is the one-word or one-figure outcome ("Correct", "+$250"). When
 * a caller passes it apart from `children`, a phone gets a layout of its own:
 * the verdict and Next share the top row, and the details, setup reading and
 * note each take a full-width row under them, instead of the whole sentence
 * wrapping around with Next stranded on a row of its own. From `sm` it sits
 * first in the one-line sentence, exactly where it was inside `children`.
 *
 * `tone` reads the verdict as a left border instead of the flat grey the
 * banner otherwise defaults to: "up" for Correct or a winning trade, "down"
 * for Wrong or a loss, "flat" for a trade that closed at breakeven. Omitted
 * it falls back to the plain muted border, for any caller that has not been
 * updated to pass one.
 */

export type RevealBannerProps = {
  onNext: () => void;
  note?: string;
  setupLine?: ReactNode;
  tone?: "up" | "down" | "flat";
  verdict?: ReactNode;
  children: ReactNode;
};

const TONE_BORDER: Record<"up" | "down" | "flat", string> = {
  up: "border-l-up",
  down: "border-l-down",
  flat: "border-l-muted",
};

/**
 * A Round boundary as the reveal shows it. Daily and coarser Rounds are stamped
 * with a plain date and read as-is; a stamp that carries an hour component
 * (`2026-07-24T05:00Z`) has its machine punctuation, noise in a sentence a
 * player reads, spaced out to `2026-07-24 05:00`.
 */
export function formatRoundTime(stamp: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(stamp);
  return match ? `${match[1]} ${match[2]}` : stamp;
}

export function RevealBanner({
  onNext,
  note,
  setupLine,
  tone,
  verdict,
  children,
}: RevealBannerProps) {
  return (
    <div
      role="status"
      // Below `sm`, a compact strip: the verdict, the sentence and the setup
      // stack in a narrow left column at `text-xs`, with Next centred beside
      // them, so the banner stays a few short lines under the chart rather
      // than a card as tall as it. From `sm`, one flex row with the sentence
      // wrapping inside its own half and Next held on the right. The verdict reads as a left border in the outcome's colour
      // rather than a background tint: the app's flat taste
      // (`directionButtonClass`) never fills a control, only outlines and
      // text carry colour.
      className={`ca-reveal flex items-center justify-between gap-3 rounded border border-muted/30 bg-surface px-3 py-2 font-mono text-xs sm:gap-x-6 sm:gap-y-2 sm:px-5 sm:py-4 sm:text-sm ${tone ? `border-l-2 ${TONE_BORDER[tone]}` : ""}`}
    >
      {/* A column below `sm`; from `sm` the flex half the sentence wraps
          inside, so Next keeps the banner's line instead of being pushed
          under a long setup reading. */}
      <div className="flex min-w-0 flex-1 flex-col gap-y-0.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
        {verdict && <span className="min-w-0">{verdict}</span>}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 sm:contents">
          {children}
        </div>
        {setupLine && (
          <span className="text-muted">
            {/* The joining dot only reads mid-sentence; on its own phone row
                it would be a stray bullet. */}
            <span className="mr-2 hidden text-dim sm:inline" aria-hidden="true">
              ·
            </span>
            {setupLine}
          </span>
        )}
        {note && <span className="italic text-dim">&quot;{note}&quot;</span>}
      </div>
      <div className="flex shrink-0 items-center justify-end gap-4">
        <button
          type="button"
          onClick={onNext}
          className="flex min-h-8 items-center rounded border border-accent px-3 py-1 text-accent transition-colors hover:border-accent/70 sm:min-h-6"
        >
          Next <span className="ml-1.5 opacity-70 hidden sm:inline">↵</span>
        </button>
      </div>
    </div>
  );
}
